
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../base/shared/modal/modal.service';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { TagModule } from 'primeng/tag';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { DateTime } from 'luxon';
import {
    InvoiceDTO,
    InvoiceFilterDTO,
    InvoiceOverviewDTO,
    InvoiceStatus,
    RecordPaymentDTO,
    PaymentMethod
} from '../../../base/models/models.swagger.generated';
import { InvoiceService } from './invoice.service';
import { RecordPaymentFormType } from './invoice-edit-forms';
import { SearchFieldComponent } from '../../../base/shared/search-field/search-field.component';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'app-invoice',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        TableModule,
        ToolbarModule,
        ButtonModule,
        DialogModule,
        InputTextModule,
        InputNumberModule,
        ToastModule,
        TranslateModule,
        DropdownModule,
        TagModule,
        CalendarModule,
        CheckboxModule,
        TooltipModule,
        SearchFieldComponent,
        MenuModule
    ],
    templateUrl: './invoice.component.html',
    styleUrls: ['./invoice.component.scss'],
    providers: [InvoiceService]
})
export class InvoiceComponent implements OnInit, OnDestroy {

    public overview: InvoiceOverviewDTO = new InvoiceOverviewDTO();
    public totalNoOfRecords = 0;
    private allInvoices: InvoiceDTO[] = [];

    public skip = 0;
    public take = 10;

    public selectedStatus: InvoiceStatus | null = null;
    public overdueOnly = false;
    public dateRange: Date[] = [];
    private searchText: string = '';

    public detailDialog = false;
    public selectedInvoice: InvoiceDTO | null = null;

    public paymentDialog = false;
    public paymentTargetInvoice: InvoiceDTO | null = null;
    public paymentForm: FormGroup<RecordPaymentFormType>;
    public savingPayment = false;

    public loading = false;
    private _subs: Subscription = new Subscription();

    public InvoiceStatus = InvoiceStatus;
    public exportMenuItems: MenuItem[] = [];


    public statusOptions = [
        { label: 'All', value: null },
        { label: 'Draft', value: InvoiceStatus.Draft },
        { label: 'Issued', value: InvoiceStatus.Issued },
        { label: 'Paid', value: InvoiceStatus.Paid },
        { label: 'Cancelled', value: InvoiceStatus.Cancelled },
        { label: 'Overdue', value: InvoiceStatus.Overdue },
    ];

    public paymentMethodOptions = [
        { label: 'Bank Transfer', value: PaymentMethod.BankTransfer },
        { label: 'UPI', value: PaymentMethod.UPI },
        { label: 'Cash', value: PaymentMethod.Cash },
        { label: 'Manual Adjustment', value: PaymentMethod.ManualAdjustment },
    ];

    constructor(
        private invoiceService: InvoiceService,
        private fb: FormBuilder,
        private modalService: ModalService,
        private translateService: TranslateService
    ) { }

    ngOnInit(): void {
        this.loadData();
        this.initializeExportMenu();
    }

    public loadData(): void {
        this.loading = true;
        const filter: InvoiceFilterDTO = new InvoiceFilterDTO({
            status: this.selectedStatus ?? undefined,
            overdueOnly: this.overdueOnly || undefined,
            fromDate: this.dateRange?.[0] ? DateTime.fromJSDate(this.dateRange[0]) : undefined,
            toDate: this.dateRange?.[1] ? DateTime.fromJSDate(this.dateRange[1]) : undefined,
        });

        this._subs.add(
            this.invoiceService.searchInvoices(filter, this.skip, this.take).subscribe({
                next: (res) => {
                    this.allInvoices = res.invoices || [];

                    if (this.searchText && this.searchText.trim()) {
                        this.overview.invoices = this.filterInvoicesBySearchText(this.allInvoices);
                    } else {
                        this.overview.invoices = this.allInvoices;
                    }

                    this.overview.totalInvoices = res.totalInvoices;
                    this.overview.pendingPayments = res.pendingPayments;
                    this.overview.paidInvoices = res.paidInvoices;
                    this.overview.overdueInvoices = res.overdueInvoices;

                    this.totalNoOfRecords = this.overview.invoices.length;
                    this.loading = false;
                },
                error: () => { this.loading = false; }
            })
        );
    }

    private filterInvoicesBySearchText(invoices: InvoiceDTO[]): InvoiceDTO[] {
        const searchLower = this.searchText.toLowerCase().trim();
        return invoices.filter(invoice =>
            invoice.invoiceNumber?.toLowerCase().includes(searchLower) ||
            invoice.userName?.toLowerCase().includes(searchLower) ||
            invoice.subscriptionPlanName?.toLowerCase().includes(searchLower)
        );
    }

    public onPageChange(event: any): void {
        this.skip = event.first;
        this.take = event.rows;
        this.loadData();
    }

    public applyFilters(): void {
        this.skip = 0;
        this.loadData();
    }

    public onSearchTextChanged(searchText: string): void {
        this.searchText = searchText;
        this.skip = 0;
        this.loadData();
    }

    public clearFilters(): void {
        this.selectedStatus = null;
        this.overdueOnly = false;
        this.dateRange = [];
        this.searchText = '';
        this.skip = 0;
        this.loadData();
    }

    public viewDetail(invoice: InvoiceDTO): void {
        this.selectedInvoice = invoice;
        this.detailDialog = true;

        this._subs.add(
            this.invoiceService.getInvoice(invoice.id).subscribe(full => {
                this.selectedInvoice = full;
            })
        );
    }

    public openPaymentDialog(invoice: InvoiceDTO): void {
        this.paymentTargetInvoice = invoice;
        this.paymentForm = this.fb.group<RecordPaymentFormType>({
            paymentAmount: this.fb.control<number>(invoice.totalAmount, Validators.required),
            paymentDate: this.fb.control<Date>(new Date(), Validators.required),
            paymentMethod: this.fb.control<PaymentMethod>(null!, Validators.required),
            transactionReference: this.fb.control<string>(''),
            notes: this.fb.control<string>(''),
        });
        this.paymentDialog = true;
    }

    public savePayment(): void {
        if (!this.paymentForm.valid || !this.paymentTargetInvoice) return;
        this.savingPayment = true;
        const val = this.paymentForm.getRawValue();
        const dto = new RecordPaymentDTO({
            paymentAmount: val.paymentAmount,
            paymentDate: DateTime.fromJSDate(val.paymentDate),
            paymentMethod: val.paymentMethod,
            transactionReference: val.transactionReference || undefined,
            notes: val.notes || undefined,
        });

        this._subs.add(
            this.invoiceService.recordPayment(this.paymentTargetInvoice.id, dto).subscribe({
                next: () => {
                    this.savingPayment = false;
                    this.paymentDialog = false;
                    this.loadData();
                },
                error: () => { this.savingPayment = false; }
            })
        );
    }


    public confirmCancel(invoice: InvoiceDTO): void {
        const dialog = this.modalService.showOkCancelDialog(
            'i18n.domain.invoice.messages.cancel-confirm-title',
            this.translateService.instant(
                'i18n.domain.invoice.messages.cancel-confirm',
                { invoiceNumber: invoice.invoiceNumber }
            )
        );

        dialog.onOkSubject.subscribe(() => {
            this._subs.add(
                this.invoiceService.cancelInvoice(invoice.id).subscribe(() => {
                    this.loadData();
                })
            );
        });
    }
    public getStatusSeverity(status: InvoiceStatus): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' {
        switch (status) {
            case InvoiceStatus.Paid: return 'success';
            case InvoiceStatus.Issued: return 'warning';
            case InvoiceStatus.Overdue: return 'danger';
            case InvoiceStatus.Cancelled: return 'secondary';
            default: return 'info';
        }
    }

    public getStatusLabel(status: InvoiceStatus): string {
        switch (status) {
            case InvoiceStatus.Draft: return 'Draft';
            case InvoiceStatus.Issued: return 'Issued';
            case InvoiceStatus.Paid: return 'Paid';
            case InvoiceStatus.Cancelled: return 'Cancelled';
            case InvoiceStatus.Overdue: return 'Overdue';
            default: return 'Unknown';
        }
    }

    public formatBillingPeriod(invoice: InvoiceDTO): string {
        const start = invoice.billingPeriodStart?.toFormat('dd MMM yyyy') ?? '';
        const end = invoice.billingPeriodEnd?.toFormat('dd MMM yyyy') ?? '';
        return `${start} to ${end}`;
    }

    public canRecordPayment(invoice: InvoiceDTO): boolean {
        return invoice.status === InvoiceStatus.Issued || invoice.status === InvoiceStatus.Overdue;
    }

    public canCancel(invoice: InvoiceDTO): boolean {
        return invoice.status !== InvoiceStatus.Paid && invoice.status !== InvoiceStatus.Cancelled;
    }

    public getPaymentMethodLabel(method: PaymentMethod): string {
        switch (method) {
            case PaymentMethod.BankTransfer: return 'Bank Transfer';
            case PaymentMethod.UPI: return 'UPI';
            case PaymentMethod.Cash: return 'Cash';
            case PaymentMethod.ManualAdjustment: return 'Manual Adjustment';
            default: return '—';
        }
    }

    private initializeExportMenu(): void {
        this.exportMenuItems = [
            {
                label: 'Export Invoice Summary to Excel',
                icon: 'pi pi-file-excel',
                command: () => this.exportInvoiceSummaryToExcel()
            },
            {
                label: 'Export Invoice Summary to CSV',
                icon: 'pi pi-file',
                command: () => this.exportInvoiceSummaryToCSV()
            },
            {
                label: 'Export Invoice Summary to PDF',
                icon: 'pi pi-file-pdf',
                command: () => this.exportInvoiceSummaryToPDF()
            },
            { separator: true },
            {
                label: 'Export Line Items to Excel',
                icon: 'pi pi-file-excel',
                command: () => this.exportLineItemsToExcel()
            },
            {
                label: 'Export Line Items to CSV',
                icon: 'pi pi-file',
                command: () => this.exportLineItemsToCSV()
            },
            {
                label: 'Export Line Items to PDF',
                icon: 'pi pi-file-pdf',
                command: () => this.exportLineItemsToPDF()
            }
        ];
    }

    private fetchAllInvoices(): Observable<InvoiceDTO[]> {
        const filter: InvoiceFilterDTO = new InvoiceFilterDTO({
            status: this.selectedStatus ?? undefined,
            overdueOnly: this.overdueOnly || undefined,
            fromDate: this.dateRange?.[0] ? DateTime.fromJSDate(this.dateRange[0]) : undefined,
            toDate: this.dateRange?.[1] ? DateTime.fromJSDate(this.dateRange[1]) : undefined,
        });
        return this.invoiceService.searchInvoices(filter, 0, this.totalNoOfRecords).pipe(
            map(res => {
                let invoices = res.invoices || [];
                if (this.searchText?.trim()) {
                    invoices = this.filterInvoicesBySearchText(invoices);
                }
                return invoices;
            })
        );
    }



    public exportInvoiceSummaryToExcel(): void {
        this._subs.add(
            this.fetchAllInvoices().subscribe({
                next: (invoices) => {
                    const exportData = this.prepareInvoiceSummaryExportData(invoices);
                    const worksheet = XLSX.utils.json_to_sheet(exportData);
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Summary');
                    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                    const blob = new Blob([buffer], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    });
                    saveAs(blob, `Invoice_Summary_${new Date().toISOString().split('T')[0]}.xlsx`);
                },
                error: (err) => console.error('Export Excel error:', err)
            })
        );
    }

    public exportInvoiceSummaryToCSV(): void {
        this._subs.add(
            this.fetchAllInvoices().subscribe({
                next: (invoices) => {
                    const exportData = this.prepareInvoiceSummaryExportData(invoices);
                    const worksheet = XLSX.utils.json_to_sheet(exportData);
                    const csv = XLSX.utils.sheet_to_csv(worksheet);
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    saveAs(blob, `Invoice_Summary_${new Date().toISOString().split('T')[0]}.csv`);
                },
                error: (err) => console.error('Export CSV error:', err)
            })
        );
    }

    public exportInvoiceSummaryToPDF(): void {
        this._subs.add(
            this.fetchAllInvoices().subscribe({
                next: (invoices) => this.generateInvoiceSummaryPDF(invoices),
                error: (err) => console.error('Export PDF error:', err)
            })
        );
    }

    private generateInvoiceSummaryPDF(invoices: InvoiceDTO[]): void {
        const doc = new jsPDF('l', 'mm', 'a4');

        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('Invoice Summary Report', 14, 15);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, 22);
        doc.text(`Total Invoices: ${invoices.length}`, 14, 27);

        const tableData = invoices.map(inv => [
            inv.invoiceNumber || '-',
            inv.userName || '-',
            inv.subscriptionPlanName || '-',
            this.formatBillingPeriod(inv),
            inv.invoiceDate?.toFormat('dd MMM yyyy') || '-',
            inv.dueDate?.toFormat('dd MMM yyyy') || '-',
            `₹${inv.totalAmount?.toFixed(2) || '0.00'}`,
            this.getStatusLabel(inv.status),
            inv.paymentDate?.toFormat('dd MMM yyyy') || '-',
            this.getPaymentMethodLabel(inv.paymentMethod),
        ]);

        autoTable(doc, {
            head: [['Invoice #', 'User', 'Plan', 'Billing Period', 'Invoice Date', 'Due Date', 'Amount', 'Status', 'Payment Date', 'Method']],
            body: tableData,
            startY: 32,
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            columnStyles: {
                0: { cellWidth: 28 },
                1: { cellWidth: 30 },
                2: { cellWidth: 28 },
                3: { cellWidth: 38 },
                4: { cellWidth: 24 },
                5: { cellWidth: 22 },
                6: { cellWidth: 22 },
                7: { cellWidth: 20 },
                8: { cellWidth: 24 },
                9: { cellWidth: 22 },
            }
        });

        doc.save(`Invoice_Summary_${new Date().toISOString().split('T')[0]}.pdf`);
    }

    private prepareInvoiceSummaryExportData(invoices: InvoiceDTO[]) {
        return invoices.map(inv => ({
            'Invoice Number': inv.invoiceNumber || '-',
            'User': inv.userName || '-',
            'Plan': inv.subscriptionPlanName || '-',
            'Billing Period': this.formatBillingPeriod(inv),
            'Invoice Date': inv.invoiceDate?.toFormat('dd MMM yyyy') || '-',
            'Due Date': inv.dueDate?.toFormat('dd MMM yyyy') || '-',
            'Total Amount (₹)': inv.totalAmount,
            'Status': this.getStatusLabel(inv.status),
            'Payment Date': inv.paymentDate?.toFormat('dd MMM yyyy') || '-',
            'Payment Method': this.getPaymentMethodLabel(inv.paymentMethod),
            'Transaction Ref': inv.transactionReference || '-',
            'Notes': inv.paymentNotes || '-',
        }));
    }

   

    public exportLineItemsToExcel(): void {
        this._subs.add(
            this.fetchAllInvoices().subscribe({
                next: (invoices) => {
                    const exportData = this.prepareLineItemsExportData(invoices);
                    const worksheet = XLSX.utils.json_to_sheet(exportData);
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Line Items');
                    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                    const blob = new Blob([buffer], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    });
                    saveAs(blob, `Invoice_LineItems_${new Date().toISOString().split('T')[0]}.xlsx`);
                },
                error: (err) => console.error('Export Excel error:', err)
            })
        );
    }

    public exportLineItemsToCSV(): void {
        this._subs.add(
            this.fetchAllInvoices().subscribe({
                next: (invoices) => {
                    const exportData = this.prepareLineItemsExportData(invoices);
                    const worksheet = XLSX.utils.json_to_sheet(exportData);
                    const csv = XLSX.utils.sheet_to_csv(worksheet);
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    saveAs(blob, `Invoice_LineItems_${new Date().toISOString().split('T')[0]}.csv`);
                },
                error: (err) => console.error('Export CSV error:', err)
            })
        );
    }

    public exportLineItemsToPDF(): void {
        this._subs.add(
            this.fetchAllInvoices().subscribe({
                next: (invoices) => this.generateLineItemsPDF(invoices),
                error: (err) => console.error('Export PDF error:', err)
            })
        );
    }

    private generateLineItemsPDF(invoices: InvoiceDTO[]): void {
        const doc = new jsPDF('l', 'mm', 'a4');

        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('Invoice Line Items Report', 14, 15);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, 22);

        const tableData: any[] = [];
        invoices.forEach(inv => {
            const lines = inv.invoiceLines;
            if (lines && lines.length > 0) {
                lines.forEach(line => {
                    tableData.push([
                        inv.invoiceNumber || '-',
                        inv.userName || '-',
                        inv.subscriptionPlanName || '-',
                        this.getStatusLabel(inv.status),
                        line.description || '-',
                        line.quantity?.toString() || '-',
                        `₹${line.amount?.toFixed(2) || '0.00'}`,
                    ]);
                });
            } else {
                tableData.push([
                    inv.invoiceNumber || '-',
                    inv.userName || '-',
                    inv.subscriptionPlanName || '-',
                    this.getStatusLabel(inv.status),
                    '-', '-', '-',
                ]);
            }
        });

        doc.text(`Total Line Items: ${tableData.length}`, 14, 27);

        autoTable(doc, {
            head: [['Invoice #', 'User', 'Plan', 'Status', 'Description', 'Qty', 'Amount']],
            body: tableData,
            startY: 32,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            columnStyles: {
                0: { cellWidth: 32 },
                1: { cellWidth: 35 },
                2: { cellWidth: 35 },
                3: { cellWidth: 25 },
                4: { cellWidth: 80 },
                5: { cellWidth: 18 },
                6: { cellWidth: 28 },
            }
        });

        doc.save(`Invoice_LineItems_${new Date().toISOString().split('T')[0]}.pdf`);
    }

    private prepareLineItemsExportData(invoices: InvoiceDTO[]) {
        const rows: any[] = [];
        invoices.forEach(inv => {
            const lines = inv.invoiceLines;
            if (lines && lines.length > 0) {
                lines.forEach(line => {
                    rows.push({
                        'Invoice Number': inv.invoiceNumber || '-',
                        'User': inv.userName || '-',
                        'Plan': inv.subscriptionPlanName || '-',
                        'Invoice Date': inv.invoiceDate?.toFormat('dd MMM yyyy') || '-',
                        'Status': this.getStatusLabel(inv.status),
                        'Description': line.description || '-',
                        'Quantity': line.quantity,
                        'Amount (₹)': line.amount,
                    });
                });
            } else {
                rows.push({
                    'Invoice Number': inv.invoiceNumber || '-',
                    'User': inv.userName || '-',
                    'Plan': inv.subscriptionPlanName || '-',
                    'Invoice Date': inv.invoiceDate?.toFormat('dd MMM yyyy') || '-',
                    'Status': this.getStatusLabel(inv.status),
                    'Description': '-',
                    'Quantity': '-',
                    'Amount (₹)': '-',
                });
            }
        });
        return rows;
    }

    ngOnDestroy(): void {
        this._subs?.unsubscribe();
    }
}